/**
 * src/api/authClient.ts — Thin wrapper for the /Auth* endpoints.
 *
 * These run through `fetch` directly (not harperClient) because:
 *   - `/AuthLogin`: no access token yet to send as Bearer
 *   - `/AuthRefresh`: a 401 here must NOT trigger harperClient's refresh
 *                     retry — that would recurse
 *   - `/AuthLogout`: uses the access token directly; response-path retry
 *                    would be meaningless (we're about to throw it away)
 */

import { HARPER_BASE_URL } from './harperClient';

export type AuthErrorType = 'invalid_credentials' | 'network_error' | 'timeout' | 'refresh_rejected' | 'provider_conflict';

export class AuthError extends Error {
  constructor(
    public readonly type: AuthErrorType,
    message: string,
  ) {
    super(message);
    this.name = 'AuthError';
  }
}

export interface TokenPair {
  access_token: string;
  refresh_token: string;
  /** Seconds until access_token expires. Clients use it to schedule refresh. */
  expires_in: number;
}

export interface AccessTokenOnly {
  access_token: string;
  expires_in: number;
}

const AUTH_TIMEOUT_MS = 6_000;

async function postJson(path: string, body: object, accessToken?: string | null): Promise<Response> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), AUTH_TIMEOUT_MS);

  try {
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    if (accessToken) headers['Authorization'] = `Bearer ${accessToken}`;
    return await fetch(`${HARPER_BASE_URL}${path}`, {
      method: 'POST',
      headers,
      body: JSON.stringify(body),
      signal: controller.signal,
    });
  } finally {
    clearTimeout(timer);
  }
}

export interface ClerkExchangeResult extends TokenPair {
  /** Resolved email string — passed back so callers can hand it to
   *  AuthContext.signIn without a separate Clerk API hit. */
  email: string;
}

/**
 * POST /AuthClerk — exchange a Clerk session token for a Harper
 * access+refresh pair. Server verifies the Clerk token, rotates the
 * Harper user's password, and returns tokens in the same shape as
 * /AuthLogin (plus the resolved email).
 *
 * Distinct error cases:
 *   - 401: bad / expired Clerk token → treat as "Clerk session gone,"
 *     caller should drop to Clerk sign-in.
 *   - 409: existing Customer with a different auth_provider —
 *     mapped to AuthError('provider_conflict') so LoginPage can
 *     render the server's message verbatim to the user.
 */
export async function exchangeClerkToken(clerkToken: string): Promise<ClerkExchangeResult> {
  let response: Response;
  try {
    response = await postJson('/AuthClerk', { clerkToken });
  } catch (err) {
    if (err instanceof Error && err.name === 'AbortError') {
      throw new AuthError('timeout', 'Sign-in timed out.');
    }
    throw new AuthError('network_error', 'Network error — check your connection and try again.');
  }

  if (response.status === 409) {
    let msg = 'Please sign in using your original method.';
    try {
      const body = await response.json() as { title?: string; message?: string };
      if (typeof body?.title === 'string' && body.title) msg = body.title;
      else if (typeof body?.message === 'string' && body.message) msg = body.message;
    } catch { /* use default */ }
    throw new AuthError('provider_conflict', msg);
  }

  if (response.status === 401 || response.status === 403) {
    throw new AuthError('refresh_rejected', 'Clerk session expired — please sign in again.');
  }
  if (!response.ok) {
    throw new AuthError('network_error', `Sign-in failed (${response.status}).`);
  }

  let data: Record<string, unknown>;
  try {
    data = await response.json() as Record<string, unknown>;
  } catch {
    throw new AuthError('network_error', 'Unexpected response from server.');
  }

  const accessToken = data.access_token;
  const refreshToken = data.refresh_token;
  const expiresIn = data.expires_in;
  const email = data.email;
  if (
    typeof accessToken !== 'string' ||
    typeof refreshToken !== 'string' ||
    typeof expiresIn !== 'number' ||
    typeof email !== 'string'
  ) {
    throw new AuthError('network_error', 'Server returned an incomplete response.');
  }

  return { access_token: accessToken, refresh_token: refreshToken, expires_in: expiresIn, email };
}

/** POST /AuthLogin — exchange password for an access + refresh pair. */
export async function authenticate(username: string, password: string): Promise<TokenPair> {
  let response: Response;
  try {
    response = await postJson('/AuthLogin', { username, password });
  } catch (err) {
    if (err instanceof Error && err.name === 'AbortError') {
      throw new AuthError('timeout', 'Authentication request timed out. Please try again.');
    }
    throw new AuthError('network_error', 'Network error — check your connection and try again.');
  }

  if (response.status === 401 || response.status === 403) {
    throw new AuthError('invalid_credentials', 'Invalid username or password.');
  }
  if (!response.ok) {
    throw new AuthError('network_error', `Server error (${response.status}). Please try again.`);
  }

  let data: Record<string, unknown>;
  try {
    data = await response.json() as Record<string, unknown>;
  } catch {
    throw new AuthError('network_error', 'Unexpected response from server.');
  }

  const accessToken = data.access_token;
  const refreshToken = data.refresh_token;
  const expiresIn = data.expires_in;
  if (typeof accessToken !== 'string' || typeof refreshToken !== 'string' || typeof expiresIn !== 'number') {
    throw new AuthError('network_error', 'Server returned an incomplete token pair.');
  }

  return { access_token: accessToken, refresh_token: refreshToken, expires_in: expiresIn };
}

/**
 * POST /AuthRefresh — swap a refresh token for a fresh access token. Harper
 * does not rotate refresh tokens, so the same refresh_token stays valid
 * through its 30-day window.
 *
 * Distinct error types let the caller distinguish "network down, try again"
 * from "refresh rejected, user must re-sign-in."
 */
export async function refresh(refreshToken: string): Promise<AccessTokenOnly> {
  let response: Response;
  try {
    response = await postJson('/AuthRefresh', { refresh_token: refreshToken });
  } catch (err) {
    if (err instanceof Error && err.name === 'AbortError') {
      throw new AuthError('timeout', 'Refresh request timed out.');
    }
    throw new AuthError('network_error', 'Network error refreshing session.');
  }

  if (response.status === 401 || response.status === 403) {
    throw new AuthError('refresh_rejected', 'Session expired — please sign in again.');
  }
  if (!response.ok) {
    throw new AuthError('network_error', `Refresh failed (${response.status}).`);
  }

  let data: Record<string, unknown>;
  try {
    data = await response.json() as Record<string, unknown>;
  } catch {
    throw new AuthError('network_error', 'Unexpected refresh response.');
  }

  const accessToken = data.access_token;
  const expiresIn = data.expires_in;
  if (typeof accessToken !== 'string' || typeof expiresIn !== 'number') {
    throw new AuthError('network_error', 'Refresh response missing access token.');
  }

  return { access_token: accessToken, expires_in: expiresIn };
}

/**
 * POST /AuthLogout — revoke the refresh token (device-scoped or global).
 *
 * Device-scoped (default): revokes THIS browser tab / device's refresh token.
 * Global (scope='all'):    revokes every refresh token for the user.
 *
 * Returns success even if the token was already revoked or unknown (server-
 * side idempotent). Callers should unconditionally clear local state after
 * this resolves; a network error also warrants clearing local state so the
 * user sees a sign-in page rather than a half-logged-out dashboard.
 */
export async function logout(
  accessToken: string,
  refreshToken: string,
  scope: 'device' | 'all' = 'device',
): Promise<void> {
  try {
    await postJson(
      '/AuthLogout',
      scope === 'all' ? { scope: 'all' } : { refresh_token: refreshToken },
      accessToken,
    );
  } catch {
    // Intentional: local state is the source of truth after logout. A
    // failure here is logged-only; the user is still effectively signed out
    // once we clear their tokens. Server-side the refresh token will expire
    // on its natural 30d cycle if we couldn't revoke it.
  }
}
